import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Table, Button, Form, Modal, Alert, Spinner, InputGroup, Dropdown, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faKey, faEye, faEyeSlash, faSearch, faSort, faSortUp, faSortDown, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import api, { API_URL } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

import './Accounts.css';
import '../styles/modal-shared.css';

// SKU Selector Component
const SkuSelector = ({ availableSkus, selectedSkus, onSelectionChange, disabled }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('label');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filteredSkus, setFilteredSkus] = useState([]);

  // Update filtered SKUs when search term, sort, or available SKUs change
  useEffect(() => {
    let filtered = [...availableSkus];

    // Apply search filter
    if (searchTerm) {
      const lowercaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(sku => 
        sku.label.toLowerCase().includes(lowercaseSearch)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    setFilteredSkus(filtered);
  }, [availableSkus, searchTerm, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return faSort;
    }
    return sortDirection === 'asc' ? faSortUp : faSortDown;
  };

  const isSelected = (skuId) => {
    return selectedSkus.some(sku => sku.id === skuId);
  };

  const handleSkuToggle = (sku) => {
    if (disabled) return;
    
    let newSelectedSkus;
    if (isSelected(sku.id)) {
      newSelectedSkus = selectedSkus.filter(s => s.id !== sku.id);
    } else {
      newSelectedSkus = [...selectedSkus, sku];
    }
    onSelectionChange(newSelectedSkus);
  };

  const handleSelectAll = () => {
    if (disabled) return;
    // Select all available SKUs, not just filtered ones
    onSelectionChange(availableSkus);
  };

  const handleSelectFiltered = () => {
    if (disabled) return;
    // Select only the currently filtered/visible SKUs
    const newSelected = [...selectedSkus];
    filteredSkus.forEach(sku => {
      if (!newSelected.some(s => s.id === sku.id)) {
        newSelected.push(sku);
      }
    });
    onSelectionChange(newSelected);
  };

  const handleSelectNone = () => {
    if (disabled) return;
    onSelectionChange([]);
  };

  const selectedCount = selectedSkus.length;
  const totalCount = availableSkus.length;
  const filteredCount = filteredSkus.length;

  return (
    <Form.Group>
      <Form.Label>Select SKUs ({selectedCount} of {totalCount} selected)</Form.Label>
      
      {/* Search and Bulk Actions */}
      <div className="d-flex mb-2 gap-2">
        <InputGroup className="flex-grow-1">
          <InputGroup.Text>
            <FontAwesomeIcon icon={faSearch} />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search SKUs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={disabled}
          />
        </InputGroup>
        <Dropdown as={ButtonGroup}>
          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={handleSelectAll}
            disabled={disabled || totalCount === 0}
          >
            <FontAwesomeIcon icon={faCheck} className="me-1" />
            All
          </Button>
          
          <Dropdown.Toggle 
            split 
            variant="outline-primary" 
            size="sm" 
            disabled={disabled}
          />
          
          <Dropdown.Menu>
            <Dropdown.Item 
              onClick={handleSelectFiltered}
              disabled={disabled || filteredCount === 0}
            >
              <FontAwesomeIcon icon={faCheck} className="me-2" />
              Select Filtered ({filteredCount})
            </Dropdown.Item>
            <Dropdown.Item 
              onClick={handleSelectAll}
              disabled={disabled || totalCount === 0}
            >
              <FontAwesomeIcon icon={faCheck} className="me-2" />
              Select All ({totalCount})
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item 
              onClick={handleSelectNone}
              disabled={disabled || selectedCount === 0}
            >
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              Clear Selection
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* SKU List */}
      <div className="sku-selector-container">
        <Table striped hover size="sm" className="mb-0">
          <thead className="sticky-top bg-light">
            <tr>
                             <th style={{ width: '40px' }}>
                 <Form.Check
                   type="checkbox"
                   checked={selectedCount === totalCount && totalCount > 0}
                   ref={(input) => {
                     if (input) {
                       // Show indeterminate if some items are selected but not all
                       input.indeterminate = selectedCount > 0 && selectedCount < totalCount;
                     }
                   }}
                   onChange={() => {
                     // Toggle between all selected and none selected
                     if (selectedCount === totalCount) {
                       handleSelectNone();
                     } else {
                       handleSelectAll();
                     }
                   }}
                   disabled={disabled}
                   title={
                     selectedCount === 0 ? 'Select all SKUs' :
                     selectedCount === totalCount ? 'Deselect all SKUs' :
                     `${selectedCount} of ${totalCount} selected - click to select all`
                   }
                 />
               </th>
              <th 
                className="sortable-header" 
                onClick={() => handleSort('label')}
                style={{ cursor: 'pointer' }}
              >
                SKU Description
                <FontAwesomeIcon 
                  icon={getSortIcon('label')} 
                  className="ms-2" 
                  style={{ fontSize: '0.8em' }}
                />
              </th>
            </tr>
          </thead>
          <tbody>
                         {filteredSkus.length === 0 ? (
               <tr>
                 <td colSpan={2} className="text-center text-muted py-3">
                   {searchTerm ? (
                     <>
                       No SKUs found matching "<strong>{searchTerm}</strong>"
                       <br />
                       <small>Try adjusting your search terms</small>
                     </>
                   ) : (
                     'No SKUs available'
                   )}
                 </td>
               </tr>
             ) : (
               filteredSkus.map(sku => (
                 <tr 
                   key={sku.id} 
                   onClick={() => handleSkuToggle(sku)}
                   style={{ cursor: disabled ? 'default' : 'pointer' }}
                   className={isSelected(sku.id) ? 'table-primary' : ''}
                   title={`Click to ${isSelected(sku.id) ? 'deselect' : 'select'} this SKU`}
                 >
                   <td onClick={(e) => e.stopPropagation()}>
                     <Form.Check
                       type="checkbox"
                       checked={isSelected(sku.id)}
                       onChange={() => handleSkuToggle(sku)}
                       disabled={disabled}
                       title={`${isSelected(sku.id) ? 'Deselect' : 'Select'} ${sku.label}`}
                     />
                   </td>
                   <td className="text-truncate" title={sku.label}>
                     {/* Highlight search terms */}
                     {searchTerm ? (
                       <span 
                         dangerouslySetInnerHTML={{
                           __html: sku.label.replace(
                             new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'),
                             '<mark>$1</mark>'
                           )
                         }}
                       />
                     ) : (
                       sku.label
                     )}
                   </td>
                 </tr>
               ))
             )}
          </tbody>
        </Table>
      </div>

             {/* Summary */}
       <Form.Text className="text-muted">
         {searchTerm ? (
           <>
             Showing {filteredCount} of {totalCount} SKUs matching "{searchTerm}". 
             {selectedCount > 0 && (
               <> {selectedCount} SKU{selectedCount !== 1 ? 's' : ''} selected.</>
             )}
           </>
         ) : (
           <>
             {totalCount} SKU{totalCount !== 1 ? 's' : ''} available.
             {selectedCount > 0 && (
               <> {selectedCount} selected.</>
             )}
           </>
         )}
         <br />
         Click rows to select/deselect, or use the dropdown above for bulk actions.
       </Form.Text>
    </Form.Group>
  );
};

function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedAccount, setSelectedAccount] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    Name: '',
    Address: '',
    City: '',
    State: '',
    Zip: '',
    Contact: '',
    Phone: '',
    webAccess: false,
    userName: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameValidated, setUsernameValidated] = useState(false);
  const [isFirstTimeWebAccess, setIsFirstTimeWebAccess] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [hasFullAccess, setHasFullAccess] = useState(true);
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [availableSkus, setAvailableSkus] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const observerTarget = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const searchTimeout = useRef(null);
  const usernameCheckTimeout = useRef(null);
  const passwordValidationTimeout = useRef(null);

  // Define fetchAccounts with useCallback
  const fetchAccounts = useCallback(async (page = 1, resetData = false) => {
    try {
      if (resetData) {
        setLoading(true);
        setAccounts([]); // Clear existing accounts when resetting
        setCurrentPage(1);
        setHasMore(true);
      }
      
      if (isLoading) return; // Prevent multiple simultaneous requests
      setIsLoading(true);

      console.log('Fetching page:', page, 'resetData:', resetData, 'search:', searchTerm);

      const response = await api.get(
        `/api/accounts?page=${page}&limit=50${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''}`
      );
      
      const data = response.data;
      
      if (resetData) {
        setAccounts(data.items || []);
      } else {
        // Filter out any duplicates when adding new items
        setAccounts(prevAccounts => {
          const newAccounts = [...prevAccounts];
          data.items.forEach(item => {
            if (!newAccounts.some(existing => existing.aid === item.aid)) {
              newAccounts.push(item);
            }
          });
          return newAccounts;
        });
      }
      
      setHasMore(data.pagination?.hasMore || false);
      setCurrentPage(data.pagination?.currentPage || page);
      setTotalItems(data.pagination?.total || 0);

      console.log('Fetched accounts:', {
        page,
        hasMore: data.pagination?.hasMore,
        itemsCount: data.items?.length,
        totalItems: data.pagination?.total,
        currentAccounts: data.items?.length
      });

    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError(err.message);
      setHasMore(false);
    } finally {
      if (resetData) {
        setLoading(false);
      }
      setIsLoading(false);
    }
  }, [navigate, searchTerm]);

  // Add CSS to prevent body scrolling
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Initial load effect with duplicate call prevention
  useEffect(() => {
    if (!user) {
      // Don't navigate here - AuthContext will handle redirection
      console.log('No user found, AuthContext should handle redirect');
      return;
    }
    
    // Prevent duplicate calls in React Strict Mode
    const callKey = `accounts-fetch-${user.id || user.customerId}-${Date.now()}`;
    const lastCall = sessionStorage.getItem('lastAccountsFetch');
    
    if (lastCall && (Date.now() - parseInt(lastCall)) < 2000) {
      console.log('Skipping duplicate accounts fetch');
      return;
    }
    
    sessionStorage.setItem('lastAccountsFetch', Date.now().toString());
    
    console.log('Loading accounts data...');
    fetchAccounts(1, true);
    fetchAvailableSkus();
  }, [user, navigate, fetchAccounts]);

  // Intersection observer effect
  useEffect(() => {
    if (!hasMore || loading || isLoading) return;

    const options = {
      root: document.querySelector('.table-responsive'),
      rootMargin: '100px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver(entries => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        const nextPage = currentPage + 1;
        console.log('Loading next page:', nextPage);
        fetchAccounts(nextPage, false);
      }
    }, options);

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, isLoading, currentPage, fetchAccounts]);

  const fetchAvailableSkus = async () => {
    try {
      const response = await api.get('/api/inventory/skus');

      const data = response.data;
      console.log('SKU response:', data); // Debug log

      if (!Array.isArray(data)) {
        console.error('Unexpected SKU data format:', data);
        throw new Error('Invalid SKU data format received');
      }

      setAvailableSkus(data.map(sku => ({
        id: sku.idCatalog,
        label: `${sku.itemNumber || ''} - ${sku.description || 'No description'}`
      })));
    } catch (err) {
      console.error('Error fetching SKUs:', err);
      setError(`Failed to load SKUs: ${err.message}`);
      
      // Clear SKUs to prevent UI issues
      setAvailableSkus([]);
      
      // Reset full access if we can't load SKUs
      if (selectedAccount) {
        setHasFullAccess(true);
        setSelectedSkus([]);
      }
    }
  };

  const validatePassword = (password) => {
    if (password.length < 8) return 'Password must be at least 8 characters long';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
    if (!/[!@#$%^&*]/.test(password)) return 'Password must contain at least one special character (!@#$%^&*)';
    return '';
  };

  const checkUsername = async (username) => {
    if (!username) return;
    try {
      setIsCheckingUsername(true);
      const response = await api.get(`/api/accounts/check-username/${username}`);
      
      const data = response.data;
      
      if (data.exists) {
        setUsernameError('Username already exists');
        setUsernameValidated(false);
        return false;
      }
      
      setUsernameError('');
      setUsernameValidated(true);
      return true;
    } catch (err) {
      console.error('Username check error:', err);
      if (err.message === 'Session expired') {
        setUsernameValidated(false);
        throw err; // Re-throw session errors
      }
      setUsernameError('Error checking username availability');
      setUsernameValidated(false);
      return false;
    } finally {
      setIsCheckingUsername(false);
    }
  };

  // Debounced username checking
  const debouncedCheckUsername = (username) => {
    // Clear any existing timeout
    if (usernameCheckTimeout.current) {
      clearTimeout(usernameCheckTimeout.current);
    }

    // Clear previous errors and loading state when user starts typing
    setUsernameError('');
    setIsCheckingUsername(false);
    setUsernameValidated(false);

    // Don't check if username is empty
    if (!username || !username.trim()) {
      return;
    }

    // Set a new timeout to check username after user stops typing
    usernameCheckTimeout.current = setTimeout(() => {
      checkUsername(username.trim());
    }, 500); // 500ms debounce
  };

  // Debounced password validation
  const debouncedValidatePassword = (password) => {
    // Clear any existing timeout
    if (passwordValidationTimeout.current) {
      clearTimeout(passwordValidationTimeout.current);
    }

    // Clear previous errors when user starts typing
    setPasswordError('');

    // Check if password is required (for add mode, or edit mode with first time web access)
    const isPasswordRequired = modalMode === 'add' || (modalMode === 'edit' && formData.webAccess && isFirstTimeWebAccess);

    // Don't validate if password is empty and not required
    if (!password || (!password.trim() && !isPasswordRequired)) {
      // But if it's required and empty, show error immediately
      if (isPasswordRequired && formData.webAccess && (!password || !password.trim())) {
        setPasswordError('Password is required when enabling web access');
      }
      return;
    }

    // Set a new timeout to validate password after user stops typing
    passwordValidationTimeout.current = setTimeout(() => {
      const error = validatePassword(password);
      setPasswordError(error);
    }, 300); // 300ms debounce (shorter than username since it's local validation)
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUsernameError('');
      setPasswordError('');

      if (formData.webAccess) {
        // For add mode, validate username availability
        if (modalMode === 'add' && formData.userName) {
          // If there's already a username error, don't proceed
          if (usernameError) {
            return;
          }
          
          // If username hasn't been validated yet, check it now
          if (!usernameValidated && !isCheckingUsername) {
            try {
              const isUsernameAvailable = await checkUsername(formData.userName);
              if (!isUsernameAvailable) {
                return;
              }
            } catch (err) {
              // If username check fails due to session issues, let the main request handle it
              console.warn('Username check failed during submission, proceeding:', err);
              // Don't return here - let the backend handle username validation
            }
          }
        }

        // Validate password if provided
        if (modalMode === 'add' || formData.password) {
          const passwordValidationError = validatePassword(formData.password);
          if (passwordValidationError) {
            setPasswordError(passwordValidationError);
            return;
          }
        }
      }

      let response;
      if (modalMode === 'add') {
        response = await api.post('/api/accounts', formData);
      } else {
        response = await api.put(`/api/accounts/${selectedAccount.aid}`, formData);
      }
      
      const updatedAccount = response.data;
      
      if (modalMode === 'add') {
        // For new accounts, refresh the entire list to maintain proper pagination
        await fetchAccounts(1, true);
      } else {
        // For updates, efficiently update just the changed account in the local state
        setAccounts(prevAccounts => 
          prevAccounts.map(account => 
            account.aid === updatedAccount.aid 
              ? { ...account, ...updatedAccount }
              : account
          )
        );
      }
      
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (account) => {
    // Clear any pending validations
    if (usernameCheckTimeout.current) {
      clearTimeout(usernameCheckTimeout.current);
    }
    if (passwordValidationTimeout.current) {
      clearTimeout(passwordValidationTimeout.current);
    }
    
    setModalMode('edit');
    setSelectedAccount(account);
    
    // Check if this is first time web access (no current web access or no username)
    const firstTimeWebAccess = !account.webAccess || !account.userName;
    setIsFirstTimeWebAccess(firstTimeWebAccess);
    
    setFormData({
      Name: account.Name,
      Address: account.Address,
      City: account.City,
      State: account.State,
      Zip: account.Zip,
      Contact: account.Contact,
      Phone: account.Phone,
      webAccess: account.webAccess,
      userName: account.userName || '',
      password: ''
    });
    setActiveTab('info');
    setShowModal(true);
    
    // Clear validation states
    setUsernameError('');
    setPasswordError('');
    setIsCheckingUsername(false);
    setUsernameValidated(false);
    
    // Fetch SKU access after modal is shown
    fetchSkuAccess(account.aid);
  };

  const handleAdd = () => {
    // Clear any pending validations
    if (usernameCheckTimeout.current) {
      clearTimeout(usernameCheckTimeout.current);
    }
    if (passwordValidationTimeout.current) {
      clearTimeout(passwordValidationTimeout.current);
    }
    
    setSelectedAccount(null);
    setIsFirstTimeWebAccess(false); // New account, not first time web access
    setFormData({
      Name: '',
      Address: '',
      City: '',
      State: '',
      Zip: '',
      Contact: '',
      Phone: '',
      webAccess: false,
      userName: '',
      password: ''
    });
    setModalMode('add');
    setShowModal(true);
    
    // Clear validation states
    setUsernameError('');
    setPasswordError('');
    setIsCheckingUsername(false);
    setUsernameValidated(false);
  };

  const handleCloseModal = () => {
    // Clear any pending validations
    if (usernameCheckTimeout.current) {
      clearTimeout(usernameCheckTimeout.current);
    }
    if (passwordValidationTimeout.current) {
      clearTimeout(passwordValidationTimeout.current);
    }
    
    setShowModal(false);
    setSelectedAccount(null);
    setFormData({
      Name: '',
      Address: '',
      City: '',
      State: '',
      Zip: '',
      Contact: '',
      Phone: '',
      webAccess: false,
      userName: '',
      password: ''
    });
    
    // Clear validation states
    setUsernameError('');
    setPasswordError('');
    setIsCheckingUsername(false);
    setUsernameValidated(false);
    setIsFirstTimeWebAccess(false);
  };

  const fetchSkuAccess = async (accountId) => {
    try {
      setError(null); // Clear any previous errors
      const response = await api.get(`/api/accounts/${accountId}/sku-access`);
      
      const data = response.data;
      
      setHasFullAccess(data.hasFullAccess);
      if (!data.hasFullAccess) {
        const skuObjects = data.restrictedSkus.map(skuId => {
          const sku = availableSkus.find(s => s.id === skuId);
          return sku || { id: skuId, label: `SKU ${skuId}` };
        });
        setSelectedSkus(skuObjects);
      } else {
        setSelectedSkus([]);
      }
    } catch (err) {
      console.error('Error fetching SKU access:', err);
      
      // Handle different error cases
      if (err.message.includes('401')) {
        setError('Your session has expired. The page will redirect automatically.');
        // Don't navigate here - let the API interceptor handle it
      } else if (err.message.includes('404')) {
        setError('Account not found. Please refresh the page.');
      } else {
        setError('Failed to fetch SKU access');
      }
      
      // Reset state on error
      setHasFullAccess(true);
      setSelectedSkus([]);
    }
  };

  const handleSkuAccessToggle = async (checked) => {
    if (!selectedAccount) return;

    try {
      setError(null);
      
      // Update local state immediately for better UX
      setHasFullAccess(checked);
      if (checked) {
        setSelectedSkus([]);
      }

      // Make API call without triggering loading state
      await api.put(`/api/accounts/${selectedAccount.aid}/sku-access`, {
        hasFullAccess: checked,
        restrictedSkus: []
      });

      // Update the account in the accounts list without re-fetching
      setAccounts(prevAccounts => 
        prevAccounts.map(account => 
          account.aid === selectedAccount.aid 
            ? { ...account, hasFullAccess: checked, restrictedSkus: [] }
            : account
        )
      );

    } catch (err) {
      // Revert local state on error
      setHasFullAccess(!checked);
      console.error('Error updating SKU access:', err);
      
      if (err.message.includes('401')) {
        setError('Your session has expired. The page will redirect automatically.');
        // Don't navigate here - let the API interceptor handle it
      } else if (err.message.includes('404')) {
        setError('Account not found. Please refresh the page.');
      } else {
        setError('Failed to update SKU access');
      }
    }
  };

  const handleSkuSelection = async (selected) => {
    if (!selectedAccount) return;

    try {
      setError(null);
      
      // Update local state immediately for better UX
      setSelectedSkus(selected);

      const selectedSkuIds = selected.map(sku => sku.id);
      await api.put(`/api/accounts/${selectedAccount.aid}/sku-access`, {
        hasFullAccess: false,
        restrictedSkus: selectedSkuIds
      });

      // Update the account in the accounts list without re-fetching
      setAccounts(prevAccounts => 
        prevAccounts.map(account => 
          account.aid === selectedAccount.aid 
            ? { ...account, hasFullAccess: false, restrictedSkus: selectedSkuIds }
            : account
        )
      );

    } catch (err) {
      // Revert local state on error
      if (selectedAccount) {
        fetchSkuAccess(selectedAccount.aid);
      }
      
      console.error('Error updating SKU selection:', err);
      
      if (err.message.includes('401')) {
        setError('Your session has expired. The page will redirect automatically.');
        // Don't navigate here - let the API interceptor handle it
      } else if (err.message.includes('404')) {
        setError('Account not found. Please refresh the page.');
      } else if (err.message.includes('400')) {
        setError('Invalid SKU selection');
      } else {
        setError('Failed to update SKU access');
      }
    }
  };

  // Handle search input changes
  const handleSearch = (value) => {
    setSearchTerm(value);
    
    // Clear the existing timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Set a new timeout to fetch data
    searchTimeout.current = setTimeout(() => {
      fetchAccounts(1, true);
    }, 300); // 300ms debounce
  };

  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      if (usernameCheckTimeout.current) {
        clearTimeout(usernameCheckTimeout.current);
      }
      if (passwordValidationTimeout.current) {
        clearTimeout(passwordValidationTimeout.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid className="h-100 d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Customer Accounts</h2>
        <div className="d-flex align-items-center">
          <InputGroup className="me-3" style={{ width: '300px' }}>
            <InputGroup.Text className="bg-white">
              <FontAwesomeIcon icon={faSearch} className="text-muted" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="border-start-0"
            />
          </InputGroup>
          <Button variant="primary" onClick={handleAdd}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Account
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="flex-grow-1 position-relative">
        <div className="position-absolute top-0 bottom-0 start-0 end-0 d-flex flex-column">
          <div className="table-responsive flex-grow-1" style={{ overflowY: 'auto' }}>
            <Table striped bordered hover className="mb-0 accounts-table">
              <thead className="sticky-top bg-white">
                <tr>
                  <th style={{ width: '15%' }}>Name</th>
                  <th style={{ width: '10%' }}>Contact</th>
                  <th style={{ width: '10%' }}>Phone</th>
                  <th style={{ width: '20%' }}>Address</th>
                  <th style={{ width: '10%' }}>City</th>
                  <th style={{ width: '7%' }}>State</th>
                  <th style={{ width: '8%' }}>Zip</th>
                  <th style={{ width: '8%' }}>Web Access</th>
                  <th style={{ width: '7%' }}>SKU Access</th>
                  <th style={{ width: '5%', minWidth: '50px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account, index) => (
                  <tr key={`${account.aid}-${index}`}>
                    <td className="text-truncate">{account.Name}</td>
                    <td className="text-truncate">{account.Contact}</td>
                    <td className="text-truncate">{account.Phone}</td>
                    <td className="text-truncate">{account.Address}</td>
                    <td className="text-truncate">{account.City}</td>
                    <td className="text-truncate">{account.State}</td>
                    <td className="text-truncate">{account.Zip}</td>
                    <td className="text-center">{account.webAccess ? 'Yes' : 'No'}</td>
                    <td className="text-center">
                      {account.hasFullAccess ? 'Full' : 
                        account.restrictedSkus?.length || '0'}
                    </td>
                    <td className="text-center p-0">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="mx-1 my-1"
                        onClick={() => handleEdit(account)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {/* Infinite scroll trigger */}
            {hasMore && (
              <div 
                ref={observerTarget}
                style={{ height: '50px' }}
                className="w-100 d-flex justify-content-center align-items-center"
              >
                {isLoading && <Spinner animation="border" size="sm" />}
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal 
        show={showModal} 
        onHide={handleCloseModal} 
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'add' ? 'Add New Account' : 'Edit Account'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAccount ? (
            <div>
              <div className="custom-tab-bar">
                <button
                  className={`custom-tab-btn${activeTab === 'info' ? ' active' : ''}`}
                  onClick={() => setActiveTab('info')}
                >Account Information</button>
                <button
                  className={`custom-tab-btn${activeTab === 'sku' ? ' active' : ''}`}
                  onClick={() => setActiveTab('sku')}
                >SKU Access</button>
              </div>
              <div style={{ display: activeTab === 'info' ? 'block' : 'none' }}>
                  <Form id="accountForm" onSubmit={handleSubmit} autoComplete="off">
                    <Form.Group className="mb-3">
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.Name}
                        onChange={(e) => setFormData({...formData, Name: e.target.value})}
                        required
                        autoComplete="off"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Contact Person</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.Contact}
                        onChange={(e) => setFormData({...formData, Contact: e.target.value})}
                        required
                        autoComplete="off"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.Phone}
                        onChange={(e) => setFormData({...formData, Phone: e.target.value})}
                        required
                        autoComplete="off"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Address</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.Address}
                        onChange={(e) => setFormData({...formData, Address: e.target.value})}
                        required
                        autoComplete="off"
                      />
                    </Form.Group>

                    <div className="row">
                      <div className="col">
                        <Form.Group className="mb-3">
                          <Form.Label>City</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.City}
                            onChange={(e) => setFormData({...formData, City: e.target.value})}
                            required
                            autoComplete="off"
                          />
                        </Form.Group>
                      </div>
                      <div className="col">
                        <Form.Group className="mb-3">
                          <Form.Label>State</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.State}
                            onChange={(e) => setFormData({...formData, State: e.target.value})}
                            required
                            autoComplete="off"
                          />
                        </Form.Group>
                      </div>
                      <div className="col">
                        <Form.Group className="mb-3">
                          <Form.Label>Zip</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.Zip}
                            onChange={(e) => setFormData({...formData, Zip: e.target.value})}
                            required
                            autoComplete="off"
                          />
                        </Form.Group>
                      </div>
                    </div>

                    <hr className="my-4" />
                    
                    <Form.Group controlId="webAccessToggle" className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Enable Web Access"
                        checked={formData.webAccess}
                        onChange={(e) => {
                          setFormData({...formData, webAccess: e.target.checked});
                          if (!e.target.checked) {
                            // Clear validations and timeouts when disabling web access
                            if (usernameCheckTimeout.current) {
                              clearTimeout(usernameCheckTimeout.current);
                            }
                            if (passwordValidationTimeout.current) {
                              clearTimeout(passwordValidationTimeout.current);
                            }
                            setUsernameError('');
                            setPasswordError('');
                            setUsernameValidated(false);
                          } else if (modalMode === 'edit' && selectedAccount && (!selectedAccount.webAccess || !selectedAccount.userName)) {
                            // Update first time web access flag when enabling web access
                            setIsFirstTimeWebAccess(true);
                          }
                        }}
                      />
                    </Form.Group>

                    <div className="web-access-section">
                      {formData.webAccess ? (
                        <>
                          <Form.Group className="mb-3">
                            <Form.Label>Username</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.userName}
                              onChange={(e) => {
                                setFormData({...formData, userName: e.target.value});
                                if (modalMode === 'add') {
                                  debouncedCheckUsername(e.target.value);
                                }
                              }}
                              isInvalid={!!usernameError}
                              required
                              autoComplete="off"
                              name="username_donotautofill"
                            />
                            <Form.Control.Feedback type="invalid">
                              {usernameError}
                            </Form.Control.Feedback>
                            {isCheckingUsername && (
                              <div className="text-muted mt-1">
                                <Spinner animation="border" size="sm" /> Checking username...
                              </div>
                            )}
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Password</Form.Label>
                            <div className="input-group">
                              <Form.Control
                                type={showPassword ? "text" : "password"}
                                value={formData.password}
                                onChange={(e) => {
                                  setFormData({...formData, password: e.target.value});
                                  debouncedValidatePassword(e.target.value);
                                }}
                                isInvalid={!!passwordError}
                                required={modalMode === 'add' || (modalMode === 'edit' && isFirstTimeWebAccess)}
                                placeholder={
                                  modalMode === 'add' 
                                    ? '' 
                                    : isFirstTimeWebAccess 
                                      ? 'Password required for web access' 
                                      : '(Leave blank to keep current password)'
                                }
                                autoComplete="new-password"
                                name="password_donotautofill"
                              />
                              <Button 
                                variant="outline-secondary"
                                onClick={() => setShowPassword(!showPassword)}
                                type="button"
                              >
                                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                              </Button>
                              <Form.Control.Feedback type="invalid">
                                {passwordError}
                              </Form.Control.Feedback>
                            </div>
                            {modalMode === 'add' && (
                              <Form.Text className="text-muted">
                                Password must be at least 8 characters long and contain uppercase, lowercase, 
                                number, and special character (!@#$%^&*)
                              </Form.Text>
                            )}
                          </Form.Group>
                        </>
                      ) : null}
                    </div>
                  </Form>
                </div>
              <div style={{ display: activeTab === 'sku' ? 'block' : 'none' }}>
                  <div className="mt-3">
                    <Form.Group className="mb-4">
                      <Form.Check
                        type="switch"
                        id="sku-access-switch"
                        label={hasFullAccess ? "Full SKU Access" : "Restricted SKU Access"}
                        checked={hasFullAccess}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSkuAccessToggle(e.target.checked);
                        }}
                        disabled={loading}
                      />
                    </Form.Group>

                    {!hasFullAccess && (
                      <SkuSelector
                        availableSkus={availableSkus}
                        selectedSkus={selectedSkus}
                        onSelectionChange={handleSkuSelection}
                        disabled={loading}
                      />
                    )}
                  </div>
              </div>
            </div>
          ) : (
            <Form id="accountForm" onSubmit={handleSubmit} autoComplete="off">
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.Name}
                  onChange={(e) => setFormData({...formData, Name: e.target.value})}
                  required
                  autoComplete="off"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Contact Person</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.Contact}
                  onChange={(e) => setFormData({...formData, Contact: e.target.value})}
                  required
                  autoComplete="off"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.Phone}
                  onChange={(e) => setFormData({...formData, Phone: e.target.value})}
                  required
                  autoComplete="off"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.Address}
                  onChange={(e) => setFormData({...formData, Address: e.target.value})}
                  required
                  autoComplete="off"
                />
              </Form.Group>

              <div className="row">
                <div className="col">
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.City}
                      onChange={(e) => setFormData({...formData, City: e.target.value})}
                      required
                      autoComplete="off"
                    />
                  </Form.Group>
                </div>
                <div className="col">
                  <Form.Group className="mb-3">
                    <Form.Label>State</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.State}
                      onChange={(e) => setFormData({...formData, State: e.target.value})}
                      required
                      autoComplete="off"
                    />
                  </Form.Group>
                </div>
                <div className="col">
                  <Form.Group className="mb-3">
                    <Form.Label>Zip</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.Zip}
                      onChange={(e) => setFormData({...formData, Zip: e.target.value})}
                      required
                      autoComplete="off"
                    />
                  </Form.Group>
                </div>
              </div>

              <hr className="my-4" />
              
              <Form.Group controlId="webAccessToggle" className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Enable Web Access"
                  checked={formData.webAccess}
                  onChange={(e) => {
                    setFormData({...formData, webAccess: e.target.checked});
                    if (!e.target.checked) {
                      // Clear validations and timeouts when disabling web access
                      if (usernameCheckTimeout.current) {
                        clearTimeout(usernameCheckTimeout.current);
                      }
                      if (passwordValidationTimeout.current) {
                        clearTimeout(passwordValidationTimeout.current);
                      }
                      setUsernameError('');
                      setPasswordError('');
                      setUsernameValidated(false);
                    } else if (modalMode === 'edit' && selectedAccount && (!selectedAccount.webAccess || !selectedAccount.userName)) {
                      // Update first time web access flag when enabling web access
                      setIsFirstTimeWebAccess(true);
                    }
                  }}
                />
              </Form.Group>

              <div className="web-access-section">
                {formData.webAccess ? (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Username</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.userName}
                        onChange={(e) => {
                          setFormData({...formData, userName: e.target.value});
                          if (modalMode === 'add') {
                            debouncedCheckUsername(e.target.value);
                          }
                        }}
                        isInvalid={!!usernameError}
                        required
                        autoComplete="off"
                        name="username_donotautofill"
                      />
                      <Form.Control.Feedback type="invalid">
                        {usernameError}
                      </Form.Control.Feedback>
                      {isCheckingUsername && (
                        <div className="text-muted mt-1">
                          <Spinner animation="border" size="sm" /> Checking username...
                        </div>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Password</Form.Label>
                      <div className="input-group">
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => {
                            setFormData({...formData, password: e.target.value});
                            debouncedValidatePassword(e.target.value);
                          }}
                          isInvalid={!!passwordError}
                          required={modalMode === 'add' || (modalMode === 'edit' && isFirstTimeWebAccess)}
                          placeholder={
                            modalMode === 'add' 
                              ? '' 
                              : isFirstTimeWebAccess 
                                ? 'Password required for web access' 
                                : '(Leave blank to keep current password)'
                          }
                          autoComplete="new-password"
                          name="password_donotautofill"
                        />
                        <Button 
                          variant="outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          type="button"
                        >
                          <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                        </Button>
                        <Form.Control.Feedback type="invalid">
                          {passwordError}
                        </Form.Control.Feedback>
                      </div>
                      {modalMode === 'add' && (
                        <Form.Text className="text-muted">
                          Password must be at least 8 characters long and contain uppercase, lowercase, 
                          number, and special character (!@#$%^&*)
                        </Form.Text>
                      )}
                    </Form.Group>
                  </>
                ) : null}
              </div>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            {activeTab === 'sku' ? 'Close' : 'Cancel'}
          </Button>
          {(!selectedAccount || activeTab === 'info') && (
            <Button 
              variant="primary" 
              type="submit"
              form="accountForm"
              disabled={isCheckingUsername || (formData.webAccess && (!!usernameError || !!passwordError))}
            >
              {modalMode === 'add' ? 'Add Account' : 'Save Changes'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Accounts; 